import React, { useState, useEffect } from 'react';
import '../css/VehicleEditModal.css';
import { fetch_all_profiles } from '../../api/profiles';
import { useAlert } from '../../providers/AlertProvider';
import logger from "../../utils/logger";


const VehicleEditModal = ({ theme, vehicle, isOpen, onClose, onSubmit }) => {
    const { showAlert } = useAlert();
    const [formData, setFormData] = useState({
        id: vehicle.id || '',
        reg: vehicle.reg || '',
        inspection_expiry: vehicle.inspection_expiry || '',
        inspection_interval: vehicle.inspection_interval ?? 6,
        mot_expiry: vehicle.mot_expiry || '',
        tax_expiry: vehicle.tax_expiry || '',
        on_hire: vehicle.on_hire || false,
        on_spot: vehicle.on_spot || false,
        hirer_id: vehicle.hirer_id || '',
        hire_from: vehicle.hire_from || '',
        hire_to: vehicle.hire_to || '',
        monthly_cost: vehicle.monthly_cost || '',
        london_registered: vehicle.london_registered || false,
        height: vehicle.height || '',
        status: vehicle.status || '',
        cab_phone: vehicle.cab_phone || '',
        loler_expiry: vehicle.loler_expiry || '',
        vor: vehicle.vor || false,
        chassis: vehicle.chassis || '',
        year_manufactured: vehicle.year_manufactured || '',
        supplier_ref: vehicle.supplier_ref || '',
        vtg7_required: vehicle.vtg7_required !== false,
        tacho_required: vehicle.tacho_required !== false,
        loler_required: vehicle.loler_required || false,
    });

    const [serviceCompanies, setServiceCompanies] = useState([]);

    // Update form data when vehicle prop changes
    useEffect(() => {
        if (vehicle) {
            setFormData({
                id: vehicle.id || '',
                reg: vehicle.reg || '',
                inspection_expiry: vehicle.inspection_expiry || '',
                inspection_interval: vehicle.inspection_interval ?? 6,
                mot_expiry: vehicle.mot_expiry || '',
                tax_expiry: vehicle.tax_expiry || '',
                on_hire: vehicle.on_hire || false,
                on_spot: vehicle.on_spot || false,
                hirer_id: vehicle.hirer_id || '',
                hire_from: vehicle.hire_from || '',
                hire_to: vehicle.hire_to || '',
                monthly_cost: vehicle.monthly_cost || '',
                london_registered: vehicle.london_registered || false,
                height: vehicle.height || '',
                status: vehicle.status || '',
                cab_phone: vehicle.cab_phone || '',
                loler_expiry: vehicle.loler_expiry || '',
                vor: vehicle.vor || false,
                chassis: vehicle.chassis || '',
                year_manufactured: vehicle.year_manufactured || '',
                supplier_ref: vehicle.supplier_ref || '',
                vtg7_required: vehicle.vtg7_required !== false,
                tacho_required: vehicle.tacho_required !== false,
                loler_required: vehicle.loler_required || false,
            });
        }
    }, [vehicle]);

    // Fetch service companies on component mount
    useEffect(() => {
        const fetchServiceCompanies = async () => {
            try {
                const response = await fetch_all_profiles();
                if (response.status === 200) {
                    const serviceProfiles = response.data.filter(profile => 
                        profile.group === 'SERVICE' && profile.status === 'active'
                    );
                    setServiceCompanies(serviceProfiles);
                }
            } catch (error) {
                logger.error('Error fetching service companies:', error);
            }
        };

        if (isOpen) {
            fetchServiceCompanies();
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
    
        if (name === 'year_manufactured') {
            if (!/^\d*$/.test(newValue)) return; // block if not digits
            if (newValue.length === 4) {
                newValue = parseInt(newValue, 10);
            }
        }

        if (name === 'monthly_cost') {
            // Allow empty string or valid decimal format (0.00)
            if (newValue === '' || /^\d+(\.\d{0,2})?$/.test(newValue)) {
                // Valid: empty, whole numbers, or decimals with up to 2 decimal places
                setFormData((prev) => ({ ...prev, [name]: newValue }));
            }
            return; // Don't proceed with normal flow for monthly_cost
        }

        if (name === 'height') {
            // Allow empty string or valid decimal meters (e.g. 4.2 or 4.55)
            if (newValue === '' || /^\d*(\.\d{0,2})?$/.test(newValue)) {
                setFormData((prev) => ({ ...prev, [name]: newValue }));
            }
            return;
        }
    
        setFormData((prev) => ({ ...prev, [name]: newValue }));
    };

    const handleToggleChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        // Check if monthly_cost is provided and validate format
        if (formData.monthly_cost && formData.monthly_cost.trim() !== '') {
            // Check if it's a whole number without decimal places
            if (/^\d+$/.test(formData.monthly_cost)) {
                showAlert({
                    message: 'Monthly cost format (420.00)',
                    type: 'danger',
                    buttons: [{ label: 'Got it', type: 'danger' }],
                });
                return;
            }
            // Check if it doesn't match the proper decimal format
            if (!/^\d+\.\d{2}$/.test(formData.monthly_cost)) {
                showAlert({
                    message: 'Invalid monthly cost format. Please use format like 420.00',
                    type: 'danger',
                    buttons: [{ label: 'Got it', type: 'danger' }],
                });
                return;
            }
        }

        if (formData.height && formData.height.toString().trim() !== '') {
            const h = parseFloat(formData.height);
            if (isNaN(h) || h <= 0) {
                showAlert({
                    message: 'Please enter a valid height in metres (e.g. 4.2 or 4.55)',
                    type: 'danger',
                    buttons: [{ label: 'Got it', type: 'danger' }],
                });
                return;
            }
        }
        onSubmit(formData);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div id={theme} className="overlay">
            <div className="modal-container">
                <h2>Edit Vehicle</h2>
                <form className="form one-column">
                    {/* Registration Number */}
                    <div className="input-group">
                        <label>Registration Number</label>
                        <input
                            type="text"
                            name="reg"
                            value={formData.reg}
                            onChange={handleInputChange}
                            className="input"
                        />
                    </div>

                    {/* Year Manufactured */}
                    <div className="input-group">
                        <label>Year Manufactured</label>
                        <input
                            type="text"
                            name="year_manufactured"
                            value={formData.year_manufactured}
                            onChange={handleInputChange}
                            maxLength={4}
                            className="input"
                        />
                    </div>

                    {/* Chassis Number (Trailer and MHE only)*/}
                    {(vehicle.type === 'TRAILER' || vehicle.type === 'MHE') ? (
                        <div className="input-group">
                            <label>C Number</label>
                            <input
                                type="text"
                                name="chassis"
                                value={formData.chassis}
                                onChange={handleInputChange}
                                className="input"
                            />
                        </div>
                    ) : null}

                    {/* Vehicle Height */}
                    <div className="input-group">
                        <label>Height (metres)</label>
                        <input
                            type="number"
                            name="height"
                            value={formData.height}
                            onChange={handleInputChange}
                            placeholder="e.g. 4.2 or 4.55"
                            step="0.01"
                            min="0"
                            className="input"
                        />
                    </div>

                    {/* Cab Phone (Unit and Rigid Only)*/}
                    {(vehicle.type === 'UNIT' || vehicle.type === 'RIGID') ? (
                        <div className="input-group">
                            <label>Cab Phone</label>
                            <input
                                type="text"
                                name="cab_phone"
                                value={formData.cab_phone}
                                onChange={handleInputChange}
                                className="input"
                            />
                        </div>
                    ) : null}

                    {/* Inspection Expiry */}
                    <div className="input-group">
                        <label>Inspection Expiry</label>
                        <input
                            type="date"
                            name="inspection_expiry"
                            value={formData.inspection_expiry}
                            onChange={handleInputChange}
                            className="input"
                        />
                    </div>

                    {/* Inspection Interval */}
                    <div className="input-group">
                        <label>Inspection Interval (weeks)</label>
                        <input
                            type="number"
                            name="inspection_interval"
                            value={formData.inspection_interval}
                            onChange={handleInputChange}
                            min="1"
                            max="52"
                            className="input"
                        />
                    </div>

                    {/* MOT Expiry (Not MHE)*/}
                    {vehicle.type !== 'MHE' && (
                        <div className="input-group">
                            <label>MOT Expiry</label>
                            <input
                                type="date"
                                name="mot_expiry"
                                value={formData.mot_expiry}
                                onChange={handleInputChange}
                                className="input"
                            />
                        </div>
                    )}

                    {/* Tax Expiry (Unit and Rigid only) */}
                    {(vehicle.type === 'UNIT' || vehicle.type === 'RIGID') ? (
                        <div className="input-group">
                            <label>Tax Expiry</label>
                            <input
                                type="date"
                                name="tax_expiry"
                                value={formData.tax_expiry}
                                onChange={handleInputChange}
                                className="input"
                            />
                        </div>
                    ) : null}

                    {/* Loler Expiry (Rigid and MHE only)*/}
                    {(vehicle.type === 'RIGID' || vehicle.type === 'MHE') ? (
                        <div className="input-group">
                            <label>Loler Expiry</label>
                            <input
                                type="date"
                                name="loler_expiry"
                                value={formData.loler_expiry}
                                onChange={handleInputChange}
                                className="input"
                            />
                        </div>
                    ) : null}

                    {/* On Hire */}
                    <div className="input-group">
                        <label>On Hire</label>
                        <div className="toggle-group">
                            <button
                                type="button"
                                className={formData.on_hire ? 'active' : ''}
                                onClick={() => handleToggleChange('on_hire', true)}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                className={!formData.on_hire ? 'active' : ''}
                                onClick={() => handleToggleChange('on_hire', false)}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {/* Hire Company */}
                    {formData.on_hire && (
                        <>
                            <div className="input-group">
                                <label>Spot Hire</label>
                                <div className="toggle-group">
                                    <button
                                        type="button"
                                        className={formData.on_spot ? 'active' : ''}
                                        onClick={() => handleToggleChange('on_spot', true)}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        className={!formData.on_spot ? 'active' : ''}
                                        onClick={() => handleToggleChange('on_spot', false)}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>

                            <div className="input-group">
                                <label>Hire Company</label>
                                <select
                                    name="hirer_id"
                                    value={formData.hirer_id || ''}
                                    onChange={handleInputChange}
                                    className="input"
                                >
                                    <option value="">Select a service company...</option>
                                    {serviceCompanies.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.name}
                                        </option>
                                    ))}
                                </select>
                            </div>                            
                            <div className="input-group">
                                <label>Hire From Date</label>
                                <input
                                    type="date"
                                    name="hire_from"
                                    value={formData.hire_from}
                                    onChange={handleInputChange}
                                    className="input"
                                />
                            </div>

                            <div className="input-group">
                                <label>Hire To Date</label>
                                <input
                                    type="date"
                                    name="hire_to"
                                    value={formData.hire_to}
                                    onChange={handleInputChange}
                                    className="input"
                                />
                            </div>

                            <div className="input-group">
                                <label>{formData.on_spot ? 'Daily Charge (£)' : 'Monthly Cost (£)'}</label>
                                <input
                                    type="text"
                                    name="monthly_cost"
                                    value={formData.monthly_cost}
                                    onChange={handleInputChange}
                                    placeholder="0.00"
                                    className="input"
                                />
                            </div>
                        </>
                    )}


                    {/* London Registered (Unit and Rigid only)*/}
                    {(vehicle.type === 'UNIT' || vehicle.type === 'RIGID') ? (
                        <div className="input-group">
                            <label>London Registered</label>
                            <div className="toggle-group">
                                <button
                                    type="button"
                                    className={formData.london_registered ? 'active' : ''}
                                    onClick={() => handleToggleChange('london_registered', true)}
                                >
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    className={!formData.london_registered ? 'active' : ''}
                                    onClick={() => handleToggleChange('london_registered', false)}
                                >
                                    No
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* Supplier Reference */}
                    <div className="input-group">
                        <label>Supplier Ref</label>
                        <input
                            type="text"
                            name="supplier_ref"
                            value={formData.supplier_ref}
                            onChange={handleInputChange}
                            className="input"
                        />
                    </div>

                    {/* VOR Status */}
                    <div className="input-group">
                        <label>Vehicle Off Road (VOR)</label>
                        <div className="toggle-group">
                            <button
                                type="button"
                                className={formData.vor ? 'active' : ''}
                                onClick={() => handleToggleChange('vor', true)}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                className={!formData.vor ? 'active' : ''}
                                onClick={() => handleToggleChange('vor', false)}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {/* Compliance Requirements */}
                    <div className="input-group" style={{ borderTop: '1px solid #ddd', paddingTop: 12, marginTop: 4 }}>
                        <label style={{ fontWeight: 700, color: '#1a73e8' }}>Compliance Requirements</label>
                    </div>

                    <div className="input-group">
                        <label>VTG7 Required</label>
                        <div className="toggle-group">
                            <button type="button" className={formData.vtg7_required ? 'active' : ''} onClick={() => handleToggleChange('vtg7_required', true)}>Yes</button>
                            <button type="button" className={!formData.vtg7_required ? 'active' : ''} onClick={() => handleToggleChange('vtg7_required', false)}>No</button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label>Tacho Calibration Required</label>
                        <div className="toggle-group">
                            <button type="button" className={formData.tacho_required ? 'active' : ''} onClick={() => handleToggleChange('tacho_required', true)}>Yes</button>
                            <button type="button" className={!formData.tacho_required ? 'active' : ''} onClick={() => handleToggleChange('tacho_required', false)}>No</button>
                        </div>
                    </div>

                    <div className="input-group">
                        <label>LOLER Required</label>
                        <div className="toggle-group">
                            <button type="button" className={formData.loler_required ? 'active' : ''} onClick={() => handleToggleChange('loler_required', true)}>Yes</button>
                            <button type="button" className={!formData.loler_required ? 'active' : ''} onClick={() => handleToggleChange('loler_required', false)}>No</button>
                        </div>
                    </div>

                </form>

                {/* Buttons */}
                <div className="button-group">
                    <button onClick={handleSubmit} className="submit-button">
                        Update
                    </button>
                    <button onClick={onClose} className="close-button">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VehicleEditModal;
